(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.NSPoeCalculator = api;
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  function createCalculationState() {
    var version = 0, active = null, activeVersion = null;
    return Object.freeze({
      getVersion: function () { return version; },
      markInputChanged: function () {
        version += 1;
        if (active) active.abort();
        active = null; activeVersion = null;
        return version;
      },
      beginRequest: function (controller) {
        if (active) active.abort();
        active = controller; activeVersion = version;
        return version;
      },
      acceptResponse: function (value) { return value === version && value === activeVersion; },
      finishRequest: function (value) { if (value === activeVersion) { active = null; activeVersion = null; } }
    });
  }

  function buildCatalogUrl(base, page) {
    var value = String(base || '/').trim() || '/';
    var number = Math.max(1, Math.floor(Number(page) || 1));
    if (/view=poe-calculator-data/.test(value)) return value + '&page=' + number;
    return (value.endsWith('/') ? value : value + '/') + 'collections/poe-network-switches?view=poe-calculator-data&page=' + number;
  }

  async function fetchCatalog(base, signal) {
    var products = [], pages = 1;
    for (var page = 1; page <= pages; page += 1) {
      var response = await root.fetch(buildCatalogUrl(base, page), {
        method: 'GET', credentials: 'same-origin', headers: { Accept: 'text/html, application/xhtml+xml' }, signal: signal
      });
      if (!response.ok) throw new Error('Product catalog HTTP ' + response.status);
      var payload = await response.json();
      if (!payload || !Array.isArray(payload.products)) throw new Error('Malformed product catalog');
      pages = Number(payload.pages) || 1;
      if (!Number.isInteger(pages) || pages < 1 || pages > 50) throw new Error('Invalid catalog pagination');
      products = products.concat(payload.products);
    }
    return products;
  }

  function displayChoice(value) {
    var labels = {
      managed: 'Managed', unmanaged: 'Unmanaged', commercial: 'Commercial', industrial: 'Industrial',
      'not-specified': 'Not specified', '1g-rj45': '1G RJ45 uplink', '1g-sfp': '1G SFP uplink',
      '10g-sfp-plus': '10G SFP+ uplink', combo: 'Combo uplink'
    };
    return labels[value] || String(value || 'Not specified');
  }

  function formatWatts(value) {
    return Number(value).toFixed(1);
  }

  function productGateResult(matches, unavailable) {
    if (unavailable) return 'UNAVAILABLE - catalog evaluation could not be completed';
    if (!matches) return 'Pending catalog evaluation';
    if (matches.verified && matches.verified.length) return 'PASS - all hard requirements verified';
    if (matches.conditional && matches.conditional.length) return 'CONDITIONAL - candidate specifications need confirmation';
    return 'FAIL - no catalog product meets all hard requirements';
  }

  function buildContactSummary(devices, requirements) {
    var safetyFactor = requirements.safetyFactorPercent == null ? 25 : requirements.safetyFactorPercent;
    return 'PoE calculator project\n' + devices.map(function (device) {
      return device.quantity + ' × ' + device.name + ' @ ' + device.watts + 'W each';
    }).join('\n') + '\nMaximum PD load: ' + formatWatts(requirements.rawPowerW) + 'W\nMinimum published PoE budget: ' +
      formatWatts(requirements.recommendedBudgetW) + 'W (' + safetyFactor + '% planning reserve)\nRequired: ' + requirements.requiredPorts +
      ' powered ports\nHighest PD input: ' + formatWatts(requirements.maxDeviceLoadW || 0) + 'W\nIEEE requirement: ' +
      (requirements.requiredIeeeType || 'Engineering confirmation') + '\nTypical PSE port output: ' +
      (requirements.requiredPsePowerW == null ? 'Engineering confirmation' : formatWatts(requirements.requiredPsePowerW) + 'W') +
      '\nPort class: ' + (requirements.suggestedPortClass || 'Custom / stack') + '\nMarket budget tier: ' +
      (requirements.suggestedBudgetTier || 'Custom / stack') + '\nManagement: ' + displayChoice(requirements.management) +
      '\nEnvironment: ' + displayChoice(requirements.environment) + '\nUplink: ' + displayChoice(requirements.uplink) +
      '\nProduct gate: ' + (requirements.productGateResult || requirements.primaryGateResult || 'Pending') +
      '\nEstimate only; verify negotiated power, cable loss, PSU configuration, startup peaks and the exact SKU datasheet.';
  }

  function invalidateResults(state, ui, hasCalculated) {
    var busy = Boolean(ui.submit.disabled);
    state.markInputChanged();
    ui.results.hidden = true; ui.summary.hidden = true; ui.submit.disabled = false;
    if (ui.contactSummary) ui.contactSummary.value = '';
    if (ui.contact) { ui.contact.hidden = true; ui.contact.open = false; }
    if (hasCalculated || busy) {
      ui.submit.textContent = 'Update recommendations';
      ui.status.textContent = 'Inputs changed. Calculate again to update your requirements and product links.';
    }
  }

  function analytics(action, productId) {
    if (!Array.isArray(root.dataLayer)) return;
    root.dataLayer.push({ event: 'poe_calculator_interaction', action: action, product_id: productId || undefined });
  }

  function revealContactFeedback(contact, feedback) {
    if (contact && feedback) { contact.hidden = false; contact.open = true; }
  }

  function reindexRows(calculator) {
    var rows = calculator.querySelectorAll('[data-ns-poe-device-list] [data-ns-poe-device-row]');
    rows.forEach(function (row, index) {
      ['name', 'quantity', 'watts'].forEach(function (key) {
        var field = row.querySelector('[data-ns-poe-device-' + key + ']');
        var id = 'ns-poe-device-' + key + '-' + index + '-' + calculator.getAttribute('data-section-id');
        field.id = id; field.name = 'device_' + key + '_' + index;
        row.querySelector('label[for*="device-' + key + '"]').htmlFor = id;
      });
      row.querySelector('[data-ns-poe-delete-device]').disabled = rows.length === 1;
    });
  }

  function collectDevices(calculator) {
    return Array.from(calculator.querySelectorAll('[data-ns-poe-device-list] [data-ns-poe-device-row]')).map(function (row) {
      return { name: row.querySelector('[data-ns-poe-device-name]').value.trim() || 'PoE device',
        quantity: Number(row.querySelector('[data-ns-poe-device-quantity]').value),
        watts: Number(row.querySelector('[data-ns-poe-device-watts]').value) };
    });
  }

  function collectProjectRequirements(calculator) {
    return {
      management: calculator.querySelector('[data-ns-poe-management]').value,
      environment: calculator.querySelector('[data-ns-poe-environment]').value,
      uplink: calculator.querySelector('[data-ns-poe-uplink]').value
    };
  }

  function initCalculator(calculator) {
    if (!calculator || calculator.getAttribute('data-ns-poe-ready') === 'true') return;
    var core = root.NSPoeCalculatorCore, render = root.NSPoeCalculatorRender;
    if (!core || !render) return;
    calculator.setAttribute('data-ns-poe-ready', 'true');
    var state = createCalculationState(), hasCalculated = false;
    var form = calculator.querySelector('[data-ns-poe-form]');
    var list = calculator.querySelector('[data-ns-poe-device-list]');
    var errors = calculator.querySelector('[data-ns-poe-errors]');
    var ui = {
      submit: calculator.querySelector('[data-ns-poe-submit]'),
      status: calculator.querySelector('[data-ns-poe-status]'),
      summary: calculator.querySelector('[data-ns-poe-summary]'),
      results: calculator.querySelector('[data-ns-poe-results]'),
      contact: calculator.querySelector('[data-ns-poe-contact]'),
      contactSummary: calculator.querySelector('[data-ns-poe-contact-summary]'),
      gate: calculator.querySelector('[data-ns-poe-summary-gate]')
    };
    var returnTo = calculator.querySelector('[data-ns-poe-return-to]');
    if (returnTo) returnTo.value = root.location.pathname + root.location.search + '#ns-poe-contact-' + calculator.getAttribute('data-section-id');
    revealContactFeedback(ui.contact, calculator.querySelector('[data-ns-poe-contact-feedback]'));

    function clear() { invalidateResults(state, ui, hasCalculated); errors.hidden = true; errors.textContent = ''; }
    function addRow(source) {
      var fragment = calculator.querySelector('[data-ns-poe-device-template]').content.cloneNode(true);
      var row = fragment.querySelector('[data-ns-poe-device-row]');
      if (source) ['name', 'quantity', 'watts'].forEach(function (key) {
        row.querySelector('[data-ns-poe-device-' + key + ']').value = source.querySelector('[data-ns-poe-device-' + key + ']').value;
      });
      list.appendChild(fragment); reindexRows(calculator); clear();
      row.querySelector('[data-ns-poe-device-name]').focus();
    }
    calculator.querySelector('[data-ns-poe-add-device]').addEventListener('click', function () { addRow(null); });
    list.addEventListener('click', function (event) {
      var row = event.target.closest('[data-ns-poe-device-row]');
      if (!row) return;
      if (event.target.closest('[data-ns-poe-copy-device]')) addRow(row);
      if (event.target.closest('[data-ns-poe-delete-device]') && list.children.length > 1) {
        row.remove(); reindexRows(calculator); clear();
      }
    });
    form.addEventListener('input', clear);
    ui.results.addEventListener('click', function (event) {
      var link = event.target.closest('a'), card = link && link.closest('[data-product-id]');
      if (card) analytics(link.hasAttribute('data-ns-poe-chatty') ? 'chatty_opened' : 'product_opened', card.getAttribute('data-product-id'));
    });
    form.addEventListener('submit', async function (event) {
      event.preventDefault(); errors.hidden = true;
      var devices = collectDevices(calculator), requirements;
      try {
        var project = collectProjectRequirements(calculator);
        if (!project.management) throw new Error('Select Managed or Unmanaged.');
        if (!project.environment) throw new Error('Select Commercial or Industrial.');
        requirements = Object.assign(core.calculateRequirements(devices, { headroomPercent: 25, reservedPorts: 0 }), project, { devices: devices });
      } catch (error) {
        errors.textContent = error.message; errors.hidden = false;
        ui.status.textContent = 'Check the device inputs and calculate again.'; return;
      }
      hasCalculated = true;
      var summaryValues = {
        safety: requirements.safetyFactorPercent + '%',
        devices: requirements.deviceCount,
        load: formatWatts(requirements.rawPowerW),
        budget: formatWatts(requirements.recommendedBudgetW),
        'max-device': formatWatts(requirements.maxDeviceLoadW),
        ieee: requirements.requiredIeeeType || 'Engineering confirmation',
        pse: requirements.requiredPsePowerW == null ? 'Engineering confirmation' : formatWatts(requirements.requiredPsePowerW) + 'W',
        'port-class': requirements.suggestedPortClass,
        'budget-tier': requirements.suggestedBudgetTier,
        management: displayChoice(requirements.management),
        environment: displayChoice(requirements.environment),
        uplink: displayChoice(requirements.uplink),
        gate: productGateResult()
      };
      Object.keys(summaryValues).forEach(function (key) {
        ui.summary.querySelector('[data-ns-poe-summary-' + key + ']').textContent = summaryValues[key];
      });
      ui.summary.querySelector('[data-ns-poe-specification]').textContent =
        'Hard gates: at least ' + formatWatts(requirements.recommendedBudgetW) + 'W published total PoE budget and ' +
        requirements.requiredPorts + ' actual powered ports. ' +
        (requirements.requiredPsePowerW ? 'The highest-power PD requires ' + formatWatts(requirements.requiredPsePowerW) + 'W PSE output per applicable port.' : 'The highest-power PD requires engineering confirmation or local power.');
      ui.summary.hidden = false; ui.results.hidden = true;
      ui.contactSummary.value = buildContactSummary(devices, requirements); ui.contact.hidden = false;
      if (requirements.requiredPsePowerW == null) {
        requirements.productGateResult = 'FAIL - no standard IEEE single-port class identified';
        ui.gate.textContent = requirements.productGateResult;
        ui.contactSummary.value = buildContactSummary(devices, requirements);
        ui.results.innerHTML = render.renderRecommendations({ verified: [], conditional: [], manual: true });
        ui.results.hidden = false;
        ui.status.textContent = 'Power classification needs engineering confirmation. No automatic product match is shown.';
        ui.submit.textContent = 'Calculate & recommend switches';
        analytics('calculation_needs_confirmation');
        return;
      }
      ui.submit.disabled = true; ui.status.textContent = 'Requirements calculated. Finding matching site products…';
      analytics('calculation_started');
      var abortController = new root.AbortController(), version = state.beginRequest(abortController);
      try {
        var products = await fetchCatalog(calculator.getAttribute('data-catalog-url') || '/', abortController.signal);
        if (!state.acceptResponse(version)) return;
        var matches = core.recommendationMatches(products, requirements);
        requirements.productGateResult = productGateResult(matches);
        ui.gate.textContent = requirements.productGateResult;
        ui.contactSummary.value = buildContactSummary(devices, requirements);
        ui.results.innerHTML = render.renderRecommendations(matches); ui.results.hidden = false;
        ui.status.textContent = matches.verified.length + ' verified and ' + matches.conditional.length + ' conditional product links found. No form submission is required.';
        ui.submit.textContent = 'Calculate & recommend switches'; analytics('calculation_completed');
      } catch (error) {
        if (error.name === 'AbortError' || !state.acceptResponse(version)) return;
        requirements.productGateResult = productGateResult(null, true);
        ui.gate.textContent = requirements.productGateResult;
        ui.contactSummary.value = buildContactSummary(devices, requirements);
        errors.textContent = 'Product links could not be loaded. Your calculated requirements are available below; retry or use the optional form.';
        errors.hidden = false; ui.status.textContent = 'No older product recommendations are shown.'; analytics('calculation_failed');
      } finally {
        if (state.acceptResponse(version)) ui.submit.disabled = false;
        state.finishRequest(version);
      }
    });
    reindexRows(calculator);
  }

  function boot() {
    if (root.document) root.document.querySelectorAll('[data-ns-poe-calculator]').forEach(initCalculator);
  }
  if (root.document) {
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
  }
  return Object.freeze({ createCalculationState: createCalculationState, buildCatalogUrl: buildCatalogUrl,
    fetchCatalog: fetchCatalog, buildContactSummary: buildContactSummary, invalidateResults: invalidateResults,
    revealContactFeedback: revealContactFeedback, collectProjectRequirements: collectProjectRequirements,
    displayChoice: displayChoice, productGateResult: productGateResult, initCalculator: initCalculator });
});
